import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import toast from "react-hot-toast";

import {
    faCircleMinus,
    faEllipsis,
    faPlus,
    faArrowDownShortWide,
    faArrowDownWideShort,
} from "@fortawesome/pro-regular-svg-icons";
import InputField from "../InputField";
import CustomDropdown from "../CustomDropdown";
import ModalBase from "../ModalBase";
import Dropdown from "../Dropdown";
import LoadingSpinner from "../LoadingSpinner";
import { getCharingSitePriceByChargingSite } from "../../api/Partners";
import { useAuth } from "../../providers/AuthContext";

const getSitePriceKey = (sitePrice) =>
    [
        sitePrice?.priceCategory ?? "",
        sitePrice?.priceValueType ?? "",
        String(sitePrice?.priceValue ?? ""),
    ].join("|");

const getPriceCategoryLabel = (category, t) =>
    t(`partner.priceCategory.${category}`, { defaultValue: category });

const getPriceValueTypeLabel = (valueType, t) =>
    t(`partner.priceValueType.${valueType}`, { defaultValue: valueType });

const SitePriceTables = ({
    addModal,
    setAddModal,
    setAddSitePriceState,
    addSitePriceState,
    sites,
    sitePrices,
    setSitePrices,
    setConfirmState,
    setHasChanges,
}) => {
    const { t } = useTranslation();
    const [loadingStates, setLoadingStates] = useState({});

    const handleSetSitePrice = (sitePrice) => {
        setSitePrices((prev) => {
            const next = [...prev];
            const index = next.findIndex(
                (s) => s.site.chargingSitePublicId === sitePrice.site.chargingSitePublicId
            );

            if (index > -1) {
                next[index] = sitePrice;
                return next;
            }

            return [...next, sitePrice];
        });
    };

    const handleLoadingChange = (sitePublicId, isLoading) => {
        setLoadingStates((prev) => ({
            ...prev,
            [sitePublicId]: isLoading,
        }));
    };

    const allSitesLoaded =
        sites.length === 0 ||
        sites.every((site) => loadingStates[site.chargingSitePublicId] === false);

    return (
        <div className="relative flex flex-col gap-10 w-full">
            {sites.map((site) => (
                <SitePriceTable
                    key={site.chargingSitePublicId}
                    addModal={addModal}
                    setAddModal={setAddModal}
                    setAddSitePriceState={setAddSitePriceState}
                    addSitePriceState={addSitePriceState}
                    site={site}
                    setConfirmState={setConfirmState}
                    sitePricesState={sitePrices}
                    setSitePricesState={handleSetSitePrice}
                    setHasChanges={setHasChanges}
                    onLoadingChange={handleLoadingChange}
                />
            ))}

            {!allSitesLoaded && (
                <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/80 pt-8">
                    <LoadingSpinner text={t("partner.loadingPrices")} className="py-4" />
                </div>
            )}
        </div>
    );
};

const SitePriceTable = ({
    setAddSitePriceState,
    site,
    setConfirmState,
    setSitePricesState,
    setHasChanges,
    onLoadingChange,
}) => {
    const { getAccessToken } = useAuth();
    const { t } = useTranslation();
    const [filteredSites, setFilteredSites] = useState([]);
    const [sortColumn, setSortColumn] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [search] = useState("");
    const [sitePrices, setSitePrices] = useState([]);
    const [addModal, setAddModal] = useState(false);

    useEffect(() => {
        setSitePricesState({ site: site, sitePrices: sitePrices });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sitePrices]);

    useEffect(() => {
        applyFilterAndSort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, sitePrices, sortColumn, sortOrder]);

    useEffect(() => {
        handleGetSitesForPartner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [site]);

    const handleGetSitesForPartner = async () => {
        onLoadingChange?.(site.chargingSitePublicId, true);
        try {
            const accessToken = await getAccessToken();
            const prices = await getCharingSitePriceByChargingSite(
                site.chargingSitePublicId,
                accessToken
            );
            setSitePrices(prices);
        } catch (error) {
            console.error(error);
        } finally {
            onLoadingChange?.(site.chargingSitePublicId, false);
        }
    };

    const addSite = async (newSite) => {
        setHasChanges(true);
        setSitePrices((d) => {
            const newSiteKey = getSitePriceKey(newSite);
            const exists = d.some(
                (item) =>
                    item.isDeleted !== true &&
                    getSitePriceKey(item) === newSiteKey
            );

            if (exists) {
                return d;
            }

            return [
                ...d,
                { ...newSite, isNew: true, chargingSitePublicId: site.chargingSitePublicId },
            ];
        });
        setAddModal(false);
        setAddSitePriceState([]);
    };

    const removeSite = (sitePrice) => {
        setHasChanges(true);
        setSitePrices((prev) =>
            prev
                .map((sp) => {
                    // If it's a new site that hasn't been saved yet (isNew), remove it completely.
                    if (sitePrice.isNew && sp.tempId === sitePrice.tempId) {
                        return null;
                    }

                    // Otherwise, mark the site as isDeleted
                    if (
                        sp.partnerChargingSitePricePublicId ===
                        sitePrice.partnerChargingSitePricePublicId
                    ) {
                        return { ...sp, isDeleted: true };
                    }

                    return sp;
                })
                .filter(Boolean)
        );
    };

    const applyFilterAndSort = () => {
        let filtered = sitePrices.filter((i) => !i.isDeleted);

        // Filter based on search
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(
                (item) =>
                    item.priceCategory?.toLowerCase().includes(searchLower) ||
                    getPriceCategoryLabel(item.priceCategory, t).toLowerCase().includes(searchLower) ||
                    item.priceValueType?.toLowerCase().includes(searchLower) ||
                    getPriceValueTypeLabel(item.priceValueType, t).toLowerCase().includes(searchLower) ||
                    item.priceValue?.toString().toLowerCase().includes(searchLower)
            );
        }

        // Sort based on selected column
        const sorted = [...filtered].sort((a, b) => {
            if (!sortColumn) return 0;

            const aValue = a[sortColumn]?.toString().toLowerCase() ?? "";
            const bValue = b[sortColumn]?.toString().toLowerCase() ?? "";

            if (sortOrder === "asc") return aValue > bValue ? 1 : -1;
            return aValue < bValue ? 1 : -1;
        });

        setFilteredSites(sorted);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("asc");
        }
    };

    const getSortIcon = (column) => {
        if (sortColumn === column) {
            return sortOrder === "asc" ? faArrowDownShortWide : faArrowDownWideShort;
        }
        return null;
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-row justify-between items-center">
                <h4 className="mb-2">{site.name}</h4>
            </div>

            <div className="overflow-x-auto pb-[250px] mb-[-250px] pt-4">
                <table className="table-default w-full">
                    <thead>
                        <tr>
                            <th className="cursor-pointer" onClick={() => handleSort("priceCategory")}>
                                {t("partner.category")}{" "}
                                {getSortIcon("priceCategory") && (
                                    <FontAwesomeIcon icon={getSortIcon("priceCategory")} className="w-4 pl-2" />
                                )}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("priceValueType")}>
                                {t("common.type")}{" "}
                                {getSortIcon("priceValueType") && (
                                    <FontAwesomeIcon icon={getSortIcon("priceValueType")} className="w-4 pl-2" />
                                )}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("priceValue")}>
                                {t("common.value")}{" "}
                                {getSortIcon("priceValue") && (
                                    <FontAwesomeIcon icon={getSortIcon("priceValue")} className="w-4 pl-2" />
                                )}
                            </th>
                            <th>
                                <div className="flex w-full justify-end items-center">
                                    <button
                                        className="block text-sm text-primary-600 hover:text-primary-400 text-left rounded-lg"
                                        onClick={() => setAddModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faPlus} /> {t("partner.addNewSitePrice", { name: site.name })}
                                    </button>

                                    <SiteModal
                                        addModal={addModal}
                                        setAddModal={setAddModal}
                                        addSitePrice={addSite}
                                        sitePrices={filteredSites}
                                    />
                                </div>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {filteredSites.map((sp, index) => (
                            <tr key={index}>
                                <td>{getPriceCategoryLabel(sp.priceCategory, t)}</td>
                                <td>{getPriceValueTypeLabel(sp.priceValueType, t)}</td>
                                <td>
                                    {sp.priceCategory === "Energy external"
                                        ? `${sp.priceValue}kr/kWh`
                                        : sp.priceValueType === "Fixed"
                                            ? `${sp.priceValue}kr`
                                            : `${sp.priceValue}%`}
                                </td>
                                <td className="text-center flex justify-end">
                                    <CustomDropdown
                                        isRight={true}
                                        ContentComponent={() => (
                                            <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                                <button
                                                    className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                                    onClick={() => {
                                                        setConfirmState((s) => ({
                                                            ...s,
                                                            isOpen: true,
                                                            id: sp.partnerChargingSitePricePublicId
                                                                ? sp.partnerChargingSitePricePublicId
                                                                : sp.tempId,
                                                            title: t("confirm.deleteEntity", {
                                                                entity: t("entity.site"),
                                                                name: `${getPriceCategoryLabel(sp.priceCategory, t)} ${getPriceValueTypeLabel(sp.priceValueType, t)} ${sp.priceValue}`,
                                                            }),
                                                            confirm: () => {
                                                                removeSite(sp);
                                                            },
                                                        }));
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                                </button>
                                            </div>
                                        )}
                                    >
                                        <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                                            <FontAwesomeIcon icon={faEllipsis} />
                                        </div>
                                    </CustomDropdown>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

            </div>
        </div>
    );
};

export default SitePriceTables;

const SiteModal = ({ addModal, setAddModal, addSitePrice = () => { }, sitePrices }) => {
    const { t } = useTranslation();
    const [sitePrice, setSitePrice] = useState({
        priceCategory: "",
        priceValueType: "Fixed",
        priceValue: "",
    });

    const [categories, setCategories] = useState([]);

    useEffect(() => {
        const active = sitePrices.filter((s) => s.isDeleted !== true);

        const hasEnergy = active.some((s) => s.priceCategory === "Energy");
        const hasEnergyExternal = active.some((s) => s.priceCategory === "Energy external");
        const hasConnector = active.some((s) => s.priceCategory === "Connector");
        const hasSite = active.some((s) => s.priceCategory === "Site");
        const hasProfitShare = active.some((s) => s.priceCategory === "Profit share");

        let newCategories = ["Energy", "Energy external", "Connector", "Site", "Profit share"];

        if (hasEnergy) newCategories = newCategories.filter((c) => c !== "Energy");
        if (hasEnergyExternal) newCategories = newCategories.filter((c) => c !== "Energy external");
        if (hasConnector) newCategories = newCategories.filter((c) => c !== "Connector");
        if (hasSite) newCategories = newCategories.filter((c) => c !== "Site");
        if (hasProfitShare) newCategories = newCategories.filter((c) => c !== "Profit share");

        if (hasEnergy || hasEnergyExternal) newCategories = newCategories.filter((c) => c !== "Profit share");
        if (hasProfitShare) {
            newCategories = newCategories.filter((c) => c !== "Energy" && c !== "Energy external");
        }

        setCategories(newCategories);
    }, [sitePrices, addModal]);


    useEffect(() => {
        setSitePrice((prev) => ({
            ...prev,
            priceValueType: prev.priceCategory === "Profit share" ? "Percentage" : "Fixed",
        }));
    }, [sitePrice.priceCategory]);

    const handleAddSitesPrice = () => {
        const active = sitePrices.filter((s) => s.isDeleted !== true);
        const duplicate = active.some((s) => getSitePriceKey(s) === getSitePriceKey({
            ...sitePrice,
            priceValueType: sitePrice.priceCategory === "Profit share" ? "Percentage" : "Fixed",
        }));

        if (duplicate) {
            toast.error(t("partner.priceAlreadyExists"));
            return;
        }

        const hasEnergy = active.some((s) => s.priceCategory === "Energy");
        const hasEnergyExternal = active.some((s) => s.priceCategory === "Energy external");
        const hasProfitShare = active.some((s) => s.priceCategory === "Profit share");

        if ((sitePrice.priceCategory === "Energy" || sitePrice.priceCategory === "Energy external") && hasProfitShare) {
            toast.error(t("partner.cannotAddEnergy", {
                energy: getPriceCategoryLabel(sitePrice.priceCategory, t),
                profitShare: getPriceCategoryLabel("Profit share", t),
            }));
            return;
        }

        if (sitePrice.priceCategory === "Profit share" && (hasEnergy || hasEnergyExternal)) {
            toast.error(t("partner.cannotAddProfitShare", {
                energy: getPriceCategoryLabel(hasEnergy ? "Energy" : "Energy external", t),
                profitShare: getPriceCategoryLabel("Profit share", t),
            }));
            return;
        }

        const payload = {
            ...sitePrice,
            priceValueType: sitePrice.priceCategory === "Profit share" ? "Percentage" : "Fixed",
            tempId: Math.random().toString(36).substring(7),
        };

        addSitePrice(payload);

        setCategories([]);
        setSitePrice({
            priceCategory: "",
            priceValueType: "Fixed",
            priceValue: "",
        });
    };


    return (
        <ModalBase isOpen={addModal} onClose={() => setAddModal(false)} size="md" className="!overflow-visible">
            <div className="flex flex-col justify-start gap-4 items-start">
                <h3>{t("partner.createSitePrice")}</h3>

                <Dropdown
                    label={t("partner.category")}
                    options={categories.map((value) => ({
                        value,
                        label: getPriceCategoryLabel(value, t),
                    }))}
                    selectedOptions={[sitePrice.priceCategory]}
                    onSelect={(selected) => {
                        setSitePrice((prev) => ({ ...prev, priceCategory: selected }));
                    }}
                />

                {sitePrice.priceCategory && (
                    <div className="w-full">
                        <InputField
                            label={
                                sitePrice?.priceCategory === "Profit share"
                                    ? t("partner.gitosSharePercent")
                                    : sitePrice?.priceCategory === "Energy external"
                                        ? t("partner.gitosSharePerKwh")
                                        : sitePrice?.priceValueType === "Fixed"
                                            ? t("partner.priceAmountInKr")
                                            : t("partner.priceAmountInPercent")
                            }
                            isNumber={true}
                            placeholder={
                                sitePrice?.priceValueType === "Fixed"
                                    ? t("partner.enterAmountInKr")
                                    : t("partner.enterAmountInPercent")
                            }
                            value={sitePrice?.priceValue}
                            onChange={(e) => setSitePrice((prev) => ({ ...prev, priceValue: e.target.value }))}
                        />
                        {sitePrice.priceCategory === "Energy external" && (
                            <div className="text-sm text-mute-600 mt-1">
                                {t("partner.energyExternalDescription")}
                            </div>
                        )}
                    </div>
                )}

                <div className="flex gap-2 w-full justify-end">
                    <button className="btn btn-outline-primary" onClick={() => setAddModal(false)}>
                        {t("common.cancel")}
                    </button>

                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!sitePrice.priceValue}
                        onClick={handleAddSitesPrice}
                    >
                        {t("common.add")}
                    </button>
                </div>
            </div>
        </ModalBase>
    );
};
